import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useTrilhaExtra } from "@/components/Breadcrumb";
import {
  Search, Copy, ExternalLink, Lock, Unlock, Send, Mail, MessageCircle, Trash2, UserPlus, Users, LogIn,
  Download, Receipt, FileText, CalendarClock, FileCheck2, ShieldCheck, Activity, Landmark, Building2, Check,
  ChevronRight, Eye, AlertTriangle, Briefcase, Home, X, UserX,
} from "lucide-react";

const BASE = "/area-cliente/escritorio";
const BTN = "inline-flex items-center justify-center gap-2 rounded-[10px] bg-[#6938ef] text-sm font-semibold text-white transition-colors hover:bg-[#5a2fd6] disabled:pointer-events-none disabled:opacity-50";
const BTN2 = "inline-flex items-center justify-center gap-2 rounded-[10px] border border-[#e2e1ee] bg-white text-sm font-semibold text-[#4c4fc1] transition-colors hover:bg-[#F3F1FF] disabled:opacity-50";
const INPUT = "h-11 w-full rounded-[10px] border border-[#e2e1ee] bg-white px-3 text-sm text-slate-900 outline-none transition-all placeholder:text-slate-400 focus:border-[#6938ef] focus:ring-2 focus:ring-[#6938ef]/15";
const CARD = "bg-white border border-[#ececf6] rounded-[18px]";
const EYEBROW = "text-[11px] font-semibold uppercase tracking-[0.16em] text-[#6938ef]";
const MOD_INFO = {
  das: [Receipt, "Guias do Simples e status de pagamento"],
  dctf: [FileText, "DARFs gerados a partir da DCTFWeb"],
  parc: [CalendarClock, "Parcelas geradas dos parcelamentos"],
  comprovantes: [FileCheck2, "Comprovantes de pagamento da Receita"],
  cert: [ShieldCheck, "Situação e validade das certidões"],
  sitfis: [Activity, "Relatório de situação fiscal"],
  defis: [Landmark, "Recibos de entrega da DEFIS"],
};
const EV_ICON = { login: LogIn, modulo: Eye, download: Download, das_atualizada: Receipt };
const DIAS_SUMIDO = 30;

const fmtCnpj = (c) => { const d = String(c || "").replace(/\D/g, ""); return d.length === 14 ? d.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, "$1.$2.$3/$4-$5") : c; };
const iniciais = (n) => (n || "").split(/\s+/).filter((p) => p.length > 1).slice(0, 2).map((p) => p[0]).join("").toUpperCase();
const erroMsg = (e) => e?.response?.data?.detail || "Algo deu errado. Tente de novo.";
const paraData = (v) => {
  if (!v) return null;
  let t = String(v);
  if (/T\d{2}:\d{2}/.test(t) && !/(Z|[+-]\d{2}:?\d{2})$/.test(t)) t += "Z";
  const d = new Date(t);
  return isNaN(d) ? null : d;
};
const diasDesde = (v) => { const d = paraData(v); return d ? (Date.now() - d.getTime()) / 86400000 : null; };
const quando = (v) => {
  const d = paraData(v);
  if (!d) return null;
  const min = Math.floor((Date.now() - d.getTime()) / 60000);
  if (min < 1) return "agora";
  if (min < 60) return `há ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `há ${h} h`;
  const dias = Math.floor(h / 24);
  if (dias === 1) return "ontem";
  if (dias < 60) return `há ${dias} dias`;
  return `em ${d.toLocaleDateString("pt-BR")}`;
};
const dataHora = (v) => paraData(v)?.toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) || "";

// situação do acesso, calculada pelo último acesso real
const situacao = (e) => {
  if (e.status === "bloqueado") return { id: "bloqueada", label: "Bloqueada", cls: "bg-red-50 text-red-700", dot: "bg-red-500" };
  if (e.status === "nao_ativado") return { id: "nao_liberada", label: "Não liberada", cls: "bg-slate-100 text-slate-500", dot: "bg-slate-400" };
  const dias = diasDesde(e.ultimo_acesso);
  if (dias === null) return { id: "nunca", label: "Nunca entrou", cls: "bg-amber-50 text-amber-700", dot: "bg-amber-500" };
  if (dias > DIAS_SUMIDO) return { id: "sumido", label: `Sumido · ${quando(e.ultimo_acesso)}`, cls: "bg-amber-50 text-amber-700", dot: "bg-amber-500" };
  return { id: "usando", label: `Entrou ${quando(e.ultimo_acesso)}`, cls: "bg-emerald-50 text-emerald-700", dot: "bg-emerald-500" };
};
const FILTROS = [["todas", "Todas"], ["usando", "Usando"], ["nunca", "Nunca entraram"], ["sumido", "Sumidos"], ["nao_liberada", "Não liberadas"], ["bloqueada", "Bloqueadas"]];

function Selo({ e }) {
  const s = situacao(e);
  return (
    <span className={`inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full whitespace-nowrap ${s.cls}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />{s.label}
    </span>
  );
}

function Numero({ icon: Icon, label, value, hint, alerta }) {
  return (
    <div className={`${CARD} p-3.5 md:p-4 flex items-start justify-between gap-3`}>
      <div className="min-w-0">
        <div className="text-[11px] md:text-xs text-[#7a7f9a] leading-tight">{label}</div>
        <div className={`text-[24px] md:text-[30px] font-semibold leading-tight tabular-nums ${alerta ? "text-amber-600" : "text-[#101652]"}`}>{value ?? 0}</div>
        {hint && <div className="text-[11px] text-[#9a9db3] truncate">{hint}</div>}
      </div>
      <div className={`w-9 h-9 rounded-xl grid place-items-center shrink-0 ${alerta ? "bg-amber-50 text-amber-600" : "bg-[#F3F1FF] text-[#6938ef]"}`}><Icon size={17} /></div>
    </div>
  );
}

function Avatar({ nome, grande }) {
  return (
    <div className={`${grande ? "w-12 h-12 md:w-14 md:h-14 rounded-2xl text-base" : "w-10 h-10 rounded-xl text-xs"} bg-[#F3F1FF] text-[#4a2fa9] grid place-items-center font-semibold shrink-0`}>
      {iniciais(nome) || <Building2 size={16} />}
    </div>
  );
}

function Evento({ ev, mostrarEmpresa }) {
  const Icon = EV_ICON[ev.tipo] || Activity;
  const linha2 = [ev.quem, mostrarEmpresa ? ev.empresa : null, dataHora(ev.em)].filter(Boolean).join(" · ");
  return (
    <li className="flex items-center gap-3 px-3.5 py-2.5">
      <div className="w-8 h-8 rounded-lg bg-[#F3F1FF] text-[#6938ef] grid place-items-center shrink-0"><Icon size={15} /></div>
      <div className="flex-1 min-w-0">
        <div className="text-sm truncate">{ev.texto}</div>
        <div className="text-[11px] text-slate-400 truncate">{linha2}</div>
      </div>
    </li>
  );
}

function JanelaConvite({ convite, empresa, onClose, copiar }) {
  if (!convite) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose}>
      <div className="bg-white w-full sm:max-w-lg rounded-t-[22px] sm:rounded-[22px] p-5 space-y-4 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className={EYEBROW}>Convite</div>
            <h3 className="text-lg font-semibold">{empresa}</h3>
            <p className="text-xs text-slate-400">Escolha como enviar para cada pessoa.</p>
          </div>
          <button onClick={onClose} aria-label="Fechar" className="w-9 h-9 grid place-items-center rounded-lg text-slate-400 hover:bg-slate-100"><X size={18} /></button>
        </div>
        {convite.destinos.map((d) => (
          <div key={d.nome + (d.email || d.telefone)} className="rounded-[14px] border border-[#ececf6] bg-[#fafafc] p-4 space-y-3">
            <div className="text-sm font-semibold">Para {d.nome}</div>
            <p className="text-sm leading-relaxed text-slate-600">{d.mensagem}</p>
            <div className="flex flex-wrap gap-2">
              {d.telefone && <a target="_blank" rel="noreferrer" href={`https://wa.me/${d.telefone}?text=${encodeURIComponent(d.mensagem)}`} className={`${BTN} h-9 px-4`}><MessageCircle size={15} /> WhatsApp</a>}
              {d.email && <a href={`mailto:${d.email}?subject=${encodeURIComponent("Sua área do cliente")}&body=${encodeURIComponent(d.mensagem)}`} className={`${BTN2} h-9 px-4`}><Mail size={15} /> E-mail</a>}
              <button onClick={() => copiar(d.mensagem)} className={`${BTN2} h-9 px-4`}><Copy size={15} /> Copiar</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function AreaClienteEscritorio() {
  const [params, setParams] = useSearchParams();
  const tela = params.get("tela") || "inicio";
  const empId = params.get("empresa");
  const aba = params.get("aba") || "acesso";
  const ir = (novo) => { setParams(novo); window.scrollTo(0, 0); };

  const [cfg, setCfg] = useState(null);
  const [empresas, setEmpresas] = useState([]);
  const [met, setMet] = useState(null);
  const [acomp, setAcomp] = useState(null);
  const [busca, setBusca] = useState("");
  const [filtro, setFiltro] = useState("todas");
  const [contato, setContato] = useState({ nome: "", email: "", telefone: "" });
  const [convite, setConvite] = useState(null);
  const [ativ, setAtiv] = useState(null);
  const [toast, setToast] = useState(null);
  const [copiado, setCopiado] = useState(false);

  const avisar = (msg, erro = false) => setToast({ msg, erro });
  useEffect(() => { if (!toast) return; const t = setTimeout(() => setToast(null), 3500); return () => clearTimeout(t); }, [toast]);

  const carregar = async () => {
    const [c, e, m] = await Promise.all([api.get(`${BASE}/config`), api.get(`${BASE}/empresas`), api.get(`${BASE}/metricas`)]);
    setCfg(c.data); setEmpresas(e.data); setMet(m.data);
    api.get(`${BASE}/acompanhamento`).then((r) => setAcomp(r.data)).catch(() => setAcomp({ empresas: {}, modulos: [] }));
  };
  useEffect(() => { carregar().catch((e) => avisar(erroMsg(e), true)); }, []);

  const sel = empresas.find((e) => e.empresa_id === empId);
  useTrilhaExtra(
    () => ir({ tela: "inicio" }),
    tela === "carteira" ? ["Minha carteira"] : tela === "empresa" ? ["Minha carteira", sel?.nome || "Empresa"] : [],
    "Área do Cliente",
    tela === "empresa" ? [() => ir({ tela: "carteira" }), null] : [],
  );
  useEffect(() => {
    setAtiv(null);
    if (!empId) return;
    api.get(`${BASE}/empresas/${empId}/atividade`).then((r) => setAtiv(r.data)).catch(() => setAtiv({ contatos: [], eventos: [] }));
  }, [empId, empresas]);
  const acessoContato = useMemo(() => Object.fromEntries((ativ?.contatos || []).map((c) => [c.id, c.ultimo_acesso])), [ativ]);

  const resumo = useMemo(() => {
    const r = { usando: 0, nunca: 0, sumido: 0, nao_liberada: 0, bloqueada: 0, liberadas: 0 };
    empresas.forEach((e) => { const s = situacao(e).id; r[s] += 1; if (s !== "nao_liberada" && s !== "bloqueada") r.liberadas += 1; });
    return r;
  }, [empresas]);
  const atencao = useMemo(() => empresas.filter((e) => ["nunca", "sumido"].includes(situacao(e).id)), [empresas]);
  const lista = useMemo(() => {
    const t = busca.toLowerCase(), d = busca.replace(/\D/g, "");
    return empresas.filter((e) => (filtro === "todas" || situacao(e).id === filtro)
      && (!busca || (e.nome || "").toLowerCase().includes(t) || (d && (e.cnpj || "").includes(d))));
  }, [empresas, busca, filtro]);

  const acao = async (fn, ok) => { try { await fn(); if (ok) avisar(ok); await carregar(); } catch (e) { avisar(erroMsg(e), true); } };
  const salvarModulos = (mods) => {
    setEmpresas((l) => l.map((e) => (e.empresa_id === sel.empresa_id ? { ...e, modulos: mods } : e)));
    api.put(`${BASE}/empresas/${sel.empresa_id}/modulos`, { modulos: mods }).catch((e) => avisar(erroMsg(e), true));
  };
  const alternar = (mod) => salvarModulos(sel.modulos.includes(mod) ? sel.modulos.filter((m) => m !== mod) : [...sel.modulos, mod]);
  const addContato = () => acao(async () => {
    await api.post(`${BASE}/empresas/${sel.empresa_id}/contatos`, contato);
    setContato({ nome: "", email: "", telefone: "" });
  }, "Contato autorizado");
  const remContato = (uid) => acao(() => api.delete(`${BASE}/empresas/${sel.empresa_id}/contatos/${uid}`), "Contato removido");
  const gerarConvite = async (e) => {
    try {
      const r = await api.post(`${BASE}/empresas/${e.empresa_id}/convite`, {});
      setConvite({ ...r.data, empresa: e.nome });
      carregar().catch(() => {});
    } catch (err) { avisar(erroMsg(err), true); }
  };
  const bloquear = () => acao(() => api.put(`${BASE}/empresas/${sel.empresa_id}/bloqueio`, { bloqueado: sel.status !== "bloqueado" }),
    sel?.status === "bloqueado" ? "Acesso liberado" : "Acesso bloqueado");

  const link = cfg?.slug ? `${cfg.base_url || window.location.origin}/cliente/${cfg.slug}` : "";
  const copiar = (txt) => { navigator.clipboard.writeText(txt); setCopiado(true); avisar("Copiado"); setTimeout(() => setCopiado(false), 1500); };
  const todos = Object.keys(cfg?.modulos || {});
  const abrirEmpresa = (e, a = "acesso") => { setContato({ nome: "", email: "", telefone: "" }); ir({ tela: "empresa", empresa: e.empresa_id, aba: a }); };

  // ---------- navegação superior ----------
  const Nav = () => (
    <div className="flex gap-1 p-1 rounded-[12px] bg-[#f1f0f7] w-full sm:w-auto">
      {[["inicio", "Início", Home], ["carteira", "Minha carteira", Briefcase]].map(([id, nome, Icon]) => (
        <button key={id} onClick={() => ir({ tela: id })}
          className={`flex-1 sm:flex-none h-9 px-4 rounded-[9px] text-sm font-semibold inline-flex items-center justify-center gap-2 transition-colors ${tela === id || (id === "carteira" && tela === "empresa") ? "bg-white text-[#101652] shadow-sm" : "text-[#6b6f8a] hover:text-[#101652]"}`}>
          <Icon size={15} /> {nome}
        </button>
      ))}
    </div>
  );

  // ---------- TELA 1: INÍCIO ----------
  const stats = (id) => acomp?.empresas?.[id] || {};
  const liberadasLista = useMemo(() => empresas.filter((e) => !["nao_liberada", "bloqueada"].includes(situacao(e).id))
    .sort((a, b) => (paraData(b.ultimo_acesso)?.getTime() || 0) - (paraData(a.ultimo_acesso)?.getTime() || 0)), [empresas]);
  const pct = (a, b) => (b ? Math.round((a / b) * 100) : 0);
  const maxMod = Math.max(1, ...(acomp?.modulos || []).map((m) => m.total));
  const Inicio = () => (
    <div className="space-y-4">
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-2.5 md:gap-3">
        <Numero icon={Users} label="Clientes usando" value={resumo.usando} hint={`últimos ${DIAS_SUMIDO} dias`} />
        <Numero icon={UserX} label="Liberados que nunca entraram" value={resumo.nunca} hint={`de ${resumo.liberadas} liberadas`} alerta={resumo.nunca > 0} />
        <Numero icon={LogIn} label="Acessos no mês" value={met?.acessos_30d} hint="últimos 30 dias" />
        <Numero icon={Download} label="Downloads no mês" value={met?.downloads_30d} hint="últimos 30 dias" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-stretch">
        {/* ADOÇÃO */}
        <section className={`${CARD} p-4 md:p-5 flex flex-col gap-4`}>
          <div>
            <h2 className="font-semibold">Uso do portal</h2>
            <p className="text-xs text-slate-400 mt-0.5">Quantas empresas já têm acesso e quantas estão usando.</p>
          </div>
          {[["Empresas liberadas", resumo.liberadas, empresas.length, "bg-[#6938ef]"], ["Clientes usando", resumo.usando, resumo.liberadas, "bg-emerald-500"]].map(([nome, a, b, cor]) => (
            <div key={nome} className="space-y-1.5">
              <div className="flex items-baseline justify-between text-sm"><span>{nome}</span><span className="font-semibold tabular-nums">{a} de {b} <span className="text-slate-400 font-normal">· {pct(a, b)}%</span></span></div>
              <div className="h-2 rounded-full bg-[#f1f0f7] overflow-hidden"><div className={`h-full rounded-full ${cor}`} style={{ width: `${pct(a, b)}%` }} /></div>
            </div>
          ))}
          <button onClick={() => { setFiltro("nao_liberada"); setBusca(""); ir({ tela: "carteira" }); }} className={`${BTN} h-11 px-4 mt-auto`}>
            <UserPlus size={16} /> Liberar empresa <span className="font-normal opacity-80">· {resumo.nao_liberada} sem acesso</span>
          </button>
        </section>

        {/* ATENÇÃO */}
        <section className={`${CARD} overflow-hidden flex flex-col`}>
          <div className="p-4 md:p-5 pb-3">
            <div className="flex items-center gap-2"><AlertTriangle size={16} className="text-amber-500" /><h2 className="font-semibold">Precisam de atenção</h2></div>
            <p className="text-xs text-slate-400 mt-0.5">Liberadas que nunca entraram ou sumiram há mais de {DIAS_SUMIDO} dias.</p>
          </div>
          {atencao.length === 0 ? (
            <div className="px-4 md:px-5 pb-5 flex items-center gap-3 text-sm text-slate-500">
              <div className="w-9 h-9 rounded-full bg-emerald-50 text-emerald-600 grid place-items-center shrink-0"><Check size={17} /></div>
              Tudo certo: todos os clientes liberados estão usando.
            </div>
          ) : (
            <ul className="divide-y divide-[#f4f3f9] border-t border-[#f4f3f9]">
              {atencao.slice(0, 4).map((e) => (
                <li key={e.empresa_id} className="flex items-center gap-3 px-4 md:px-5 py-2.5">
                  <button onClick={() => abrirEmpresa(e)} className="flex-1 min-w-0 text-left">
                    <div className="text-sm font-semibold truncate">{e.nome}</div>
                    <div className="mt-1"><Selo e={e} /></div>
                  </button>
                  <button onClick={() => gerarConvite(e)} disabled={!e.contatos.length} className={`${BTN2} h-9 px-3 text-xs shrink-0`}><Send size={14} /> Reenviar</button>
                </li>
              ))}
              {atencao.length > 4 && (
                <li><button onClick={() => { setFiltro("nunca"); ir({ tela: "carteira" }); }} className="w-full px-5 py-3 text-xs font-semibold text-[#4c4fc1] text-left">Ver todas ({atencao.length})</button></li>
              )}
            </ul>
          )}
        </section>

        {/* MAIS CONSULTADOS */}
        <section className={`${CARD} p-4 md:p-5 space-y-3`}>
          <div>
            <h2 className="font-semibold">O que seus clientes mais consultam</h2>
            <p className="text-xs text-slate-400 mt-0.5">Aberturas e downloads nos últimos 30 dias.</p>
          </div>
          {!acomp ? <p className="text-sm text-slate-400">Carregando...</p>
            : acomp.modulos.length === 0 ? <p className="text-sm text-slate-500">Ainda sem consultas no período.</p>
            : acomp.modulos.slice(0, 5).map((m) => {
              const [Icon] = MOD_INFO[m.id] || [FileText];
              return (
                <div key={m.id} className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-[#F3F1FF] text-[#6938ef] grid place-items-center shrink-0"><Icon size={15} /></div>
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between text-sm"><span className="truncate">{cfg?.modulos?.[m.id] || m.id}</span><span className="font-semibold tabular-nums">{m.total}</span></div>
                    <div className="h-1.5 mt-1 rounded-full bg-[#f1f0f7] overflow-hidden"><div className="h-full rounded-full bg-[#6938ef]" style={{ width: `${(m.total / maxMod) * 100}%` }} /></div>
                  </div>
                </div>
              );
            })}
        </section>
      </div>

      {/* ACOMPANHAMENTO POR EMPRESA */}
      <section className={`${CARD} overflow-hidden`}>
        <div className="p-4 md:p-5 pb-3 flex flex-wrap items-end justify-between gap-2">
          <div>
            <h2 className="font-semibold">Acompanhamento por empresa</h2>
            <p className="text-xs text-slate-400 mt-0.5">Empresas liberadas e o uso de cada uma nos últimos 30 dias.</p>
          </div>
          <button onClick={() => { setFiltro("todas"); ir({ tela: "carteira" }); }} className="text-xs font-semibold text-[#4c4fc1]">Ver carteira completa</button>
        </div>
        {liberadasLista.length === 0 ? (
          <p className="px-5 pb-5 text-sm text-slate-500">Nenhuma empresa liberada ainda. Use "Liberar empresa" para começar.</p>
        ) : (
          <>
            <div className="hidden md:grid grid-cols-[minmax(0,2.2fr)_minmax(0,1.3fr)_90px_90px_minmax(0,1.2fr)_24px] gap-3 px-5 py-2 border-y border-[#f4f3f9] bg-[#fafafc] text-[11px] font-semibold uppercase tracking-[0.08em] text-[#7a7f9a]">
              <span>Empresa</span><span>Situação</span><span className="text-right">Acessos</span><span className="text-right">Downloads</span><span className="pl-4">Mais consultado</span><span />
            </div>
            <ul className="divide-y divide-[#f4f3f9] border-t md:border-t-0 border-[#f4f3f9]">
              {liberadasLista.map((e) => {
                const s = stats(e.empresa_id);
                const mais = s.mais_visto ? (cfg?.modulos?.[s.mais_visto] || s.mais_visto) : "—";
                return (
                  <li key={e.empresa_id}>
                    <button onClick={() => abrirEmpresa(e, "historico")} className="w-full text-left px-4 md:px-5 py-3 hover:bg-[#fafafc] transition-colors md:grid md:grid-cols-[minmax(0,2.2fr)_minmax(0,1.3fr)_90px_90px_minmax(0,1.2fr)_24px] md:gap-3 md:items-center flex items-center gap-3">
                      <div className="min-w-0 flex-1 md:flex-none">
                        <div className="text-sm font-semibold truncate">{e.nome}</div>
                        <div className="text-xs text-slate-400 hidden md:block">{fmtCnpj(e.cnpj)}</div>
                        <div className="md:hidden mt-1 space-y-1">
                          <Selo e={e} />
                          <div className="text-[11px] text-slate-500">{s.acessos || 0} acessos · {s.downloads || 0} downloads · {mais}</div>
                        </div>
                      </div>
                      <div className="hidden md:block"><Selo e={e} /></div>
                      <div className="hidden md:block text-right text-sm tabular-nums">{s.acessos || 0}</div>
                      <div className="hidden md:block text-right text-sm tabular-nums">{s.downloads || 0}</div>
                      <div className="hidden md:block pl-4 text-sm truncate text-slate-600">{mais}</div>
                      <ChevronRight size={16} className="shrink-0 text-[#a9a6d8]" />
                    </button>
                  </li>
                );
              })}
            </ul>
          </>
        )}
      </section>
    </div>
  );

  // ---------- TELA 2: CARTEIRA ----------
  const contagem = { todas: empresas.length, ...resumo };
  const Carteira = () => (
    <section className={`${CARD} overflow-hidden`}>
      <div className="p-4 md:p-5 space-y-3 border-b border-[#f0eff6]">
        {filtro === "nao_liberada" && <p className="text-sm text-[#4a2fa9] bg-[#F3F1FF] rounded-[10px] px-3 py-2">Escolha a empresa que você quer liberar.</p>}
        <div className="relative">
          <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input aria-label="Buscar empresa" className={`${INPUT} pl-10`} placeholder="Buscar por nome ou CNPJ" value={busca} onChange={(e) => setBusca(e.target.value)} />
        </div>
        <div className="flex gap-1.5 overflow-x-auto pb-0.5 -mx-1 px-1">
          {FILTROS.map(([id, nome]) => (
            <button key={id} onClick={() => setFiltro(id)}
              className={`h-8 px-3 rounded-full text-xs font-semibold whitespace-nowrap transition-colors ${filtro === id ? "bg-[#6938ef] text-white" : "bg-[#f4f4f7] text-[#4c4fc1] hover:bg-[#ece9fb]"}`}>
              {nome} · {contagem[id] || 0}
            </button>
          ))}
        </div>
      </div>
      {lista.length === 0 && <p className="p-8 text-sm text-center text-slate-400">Nenhuma empresa encontrada.</p>}
      <ul className="divide-y divide-[#f4f3f9]">
        {lista.map((e) => (
          <li key={e.empresa_id}>
            <button onClick={() => abrirEmpresa(e)} className="w-full text-left flex items-center gap-3 px-4 md:px-5 py-3 hover:bg-[#fafafc] transition-colors">
              <Avatar nome={e.nome} />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold truncate">{e.nome}</div>
                <div className="text-xs text-slate-400">{fmtCnpj(e.cnpj)}</div>
                <div className="mt-1 sm:hidden"><Selo e={e} /></div>
              </div>
              <div className="hidden sm:block"><Selo e={e} /></div>
              <ChevronRight size={16} className="shrink-0 text-[#a9a6d8]" />
            </button>
          </li>
        ))}
      </ul>
    </section>
  );

  // ---------- TELA 3: EMPRESA ----------
  const Empresa = () => {
    if (!sel) return <section className={`${CARD} p-10 text-center text-slate-400`}>Carregando empresa...</section>;
    const liberada = sel.status !== "nao_ativado";
    return (
      <div className="space-y-4">
        <section className={`${CARD} p-4 md:p-5 flex flex-wrap items-center gap-3 md:gap-4`}>
          <Avatar nome={sel.nome} grande />
          <div className="flex-1 min-w-0">
            <h2 className="text-[18px] md:text-[22px] font-semibold tracking-[-0.02em] leading-tight">{sel.nome}</h2>
            <div className="flex flex-wrap items-center gap-2 mt-1"><span className="text-sm text-slate-400">{fmtCnpj(sel.cnpj)}</span><Selo e={sel} /></div>
          </div>
          {liberada && (
            <button onClick={bloquear} className={`${BTN2} h-10 px-4 w-full sm:w-auto text-slate-600`}>
              {sel.status === "bloqueado" ? <><Unlock size={16} /> Liberar acesso</> : <><Lock size={16} /> Bloquear acesso</>}
            </button>
          )}
        </section>

        <div className="flex gap-1 p-1 rounded-[12px] bg-[#f1f0f7]">
          {[["acesso", "Acesso"], ["permissoes", "O que ela vê"], ["historico", "Histórico"]].map(([id, nome]) => (
            <button key={id} onClick={() => setParams({ tela: "empresa", empresa: sel.empresa_id, aba: id })}
              className={`flex-1 h-9 rounded-[9px] text-sm font-semibold transition-colors ${aba === id ? "bg-white text-[#101652] shadow-sm" : "text-[#6b6f8a]"}`}>{nome}</button>
          ))}
        </div>

        {aba === "acesso" && (
          <section className={`${CARD} p-4 md:p-5 space-y-4`}>
            <div>
              <h3 className="font-semibold">Quem pode entrar</h3>
              <p className="text-xs text-slate-400">A pessoa entra com o e-mail ou celular cadastrado e recebe um código. Não precisa de senha.</p>
            </div>
            {sel.contatos.length === 0 && <p className="text-sm text-slate-500 rounded-[12px] bg-[#fafafc] border border-dashed border-[#e2e1ee] p-3">Ninguém autorizado ainda. Cadastre abaixo quem vai acessar.</p>}
            <div className="space-y-2">
              {sel.contatos.map((c) => (
                <div key={c.id} className="flex items-center gap-3 p-3 rounded-[14px] bg-[#fafafc] border border-[#ececf6]">
                  <div className="w-9 h-9 rounded-full bg-[#F3F1FF] text-[#4a2fa9] grid place-items-center text-xs font-semibold shrink-0">{iniciais(c.nome)}</div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold truncate">{c.nome}</div>
                    <div className="text-xs text-slate-400 truncate">{c.contato}</div>
                    {ativ && <div className={`text-[11px] ${acessoContato[c.id] ? "text-emerald-600" : "text-amber-600"}`}>{acessoContato[c.id] ? `Último acesso ${quando(acessoContato[c.id])}` : "Ainda não entrou"}</div>}
                  </div>
                  <button onClick={() => remContato(c.id)} aria-label={`Remover ${c.nome}`} className="w-9 h-9 grid place-items-center rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50"><Trash2 size={16} /></button>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <input aria-label="Nome" className={INPUT} placeholder="Nome" value={contato.nome} onChange={(e) => setContato({ ...contato, nome: e.target.value })} />
              <input aria-label="E-mail" className={INPUT} placeholder="E-mail" value={contato.email} onChange={(e) => setContato({ ...contato, email: e.target.value })} />
              <input aria-label="Celular" className={INPUT} placeholder="Celular (WhatsApp)" value={contato.telefone} onChange={(e) => setContato({ ...contato, telefone: e.target.value })} />
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <button onClick={addContato} disabled={!contato.nome || (!contato.email && !contato.telefone)} className={`${BTN2} h-11 px-5`}><UserPlus size={16} /> Autorizar pessoa</button>
              <button onClick={() => gerarConvite(sel)} disabled={!sel.contatos.length} className={`${BTN} h-11 px-5 sm:ml-auto`}><Send size={16} /> {liberada ? "Enviar convite de novo" : "Liberar e enviar convite"}</button>
            </div>
          </section>
        )}

        {aba === "permissoes" && (
          <section className={`${CARD} p-4 md:p-5 space-y-4`}>
            <div className="flex flex-wrap items-end justify-between gap-2">
              <div>
                <h3 className="font-semibold">O que o cliente pode ver</h3>
                <p className="text-xs text-slate-400">{sel.modulos.length} de {todos.length} liberadas · salva na hora</p>
              </div>
              <div className="flex gap-2">
                <button onClick={() => salvarModulos(todos)} className="h-8 px-3 rounded-full text-xs font-semibold bg-[#F3F1FF] text-[#4a2fa9]">Liberar todas</button>
                <button onClick={() => salvarModulos([])} className="h-8 px-3 rounded-full text-xs font-semibold bg-[#f4f4f7] text-slate-500">Limpar</button>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              {todos.map((id) => {
                const on = sel.modulos.includes(id);
                const [Icon, desc] = MOD_INFO[id] || [FileText, ""];
                return (
                  <button key={id} role="switch" aria-checked={on} onClick={() => alternar(id)}
                    className={`text-left flex items-center gap-3 p-3.5 rounded-[14px] border transition-colors ${on ? "border-[#c9bcff] bg-[#faf9ff]" : "border-[#ececf6] bg-white"}`}>
                    <div className={`w-10 h-10 rounded-xl grid place-items-center shrink-0 ${on ? "bg-[#6938ef] text-white" : "bg-[#f4f4f7] text-slate-400"}`}><Icon size={18} /></div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold">{cfg.modulos[id]}</div>
                      <div className="text-xs text-slate-400 truncate">{desc}</div>
                    </div>
                    <span className={`w-10 h-6 rounded-full p-0.5 flex shrink-0 transition-colors ${on ? "bg-[#6938ef] justify-end" : "bg-slate-200 justify-start"}`}><span className="w-5 h-5 rounded-full bg-white shadow-sm" /></span>
                  </button>
                );
              })}
            </div>
          </section>
        )}

        {aba === "historico" && (
          <section className={`${CARD} overflow-hidden`}>
            <div className="p-4 md:p-5 pb-3">
              <h3 className="font-semibold">Histórico do cliente</h3>
              <p className="text-xs text-slate-400">Entradas, documentos abertos e downloads.</p>
            </div>
            {!ativ ? <p className="px-5 pb-5 text-sm text-slate-400">Carregando...</p>
              : ativ.eventos.length === 0 ? <p className="px-5 pb-5 text-sm text-slate-500">Ninguém desta empresa entrou ainda.</p>
              : <ul className="divide-y divide-[#f4f3f9] border-t border-[#f4f3f9]">{ativ.eventos.map((ev, i) => <Evento key={i} ev={ev} />)}</ul>}
          </section>
        )}
      </div>
    );
  };

  return (
    <div className="w-full px-3 sm:px-4 md:px-6 py-4 md:py-6 space-y-4 text-[#101652] font-normal antialiased">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
        <h1 className="text-[24px] md:text-[30px] font-semibold leading-tight tracking-[-0.03em]">Área do Cliente</h1>
        <div className={`flex flex-col sm:flex-row gap-2 sm:items-center ${tela === "empresa" ? "hidden sm:flex" : ""}`}>
          {link && (
            <div className="flex items-center gap-1.5 h-11 rounded-[12px] border border-[#ececf6] bg-white pl-3 pr-1 min-w-0">
              <span className="text-[11px] text-[#7a7f9a] shrink-0">Link dos clientes</span>
              <span className="text-sm font-semibold truncate max-w-[200px] md:max-w-[300px]">{link.replace(/^https?:\/\//, "")}</span>
              <button onClick={() => copiar(link)} aria-label="Copiar link" className={`${BTN} w-9 h-9 shrink-0`}>{copiado ? <Check size={15} /> : <Copy size={15} />}</button>
              <a href={link} target="_blank" rel="noreferrer" aria-label="Abrir área do cliente" className={`${BTN2} w-9 h-9 shrink-0`}><ExternalLink size={15} /></a>
            </div>
          )}
          <Nav />
        </div>
      </div>

      {tela === "inicio" && Inicio()}
      {tela === "carteira" && Carteira()}
      {tela === "empresa" && Empresa()}

      <JanelaConvite convite={convite} empresa={convite?.empresa} onClose={() => setConvite(null)} copiar={copiar} />

      {toast && (
        <div role="status" className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-[60] px-4 py-3 rounded-xl text-sm font-semibold shadow-lg text-white ${toast.erro ? "bg-red-600" : "bg-[#2D1B96]"}`}>
          {toast.msg}
        </div>
      )}
    </div>
  );
}
